/**
 * @module app.core
 */

import * as Core from 'app/core/models/essette-api';
import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/api/api';
import { LookupService as CoreLookupService } from 'app/core/services/lookup-service';
import { validatePostalCode } from 'app/shared/lib/validation/validation-utilities';
import { IMemberSearchFilters, MemberService } from 'app/cd/services/member-service';


@autoinject()
export class EditAddress extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay, changeHandler: 'reload' })
    public address: Core.Address = {};

    @bindable.boolean()
    public showCountry: boolean = false;

    @bindable.boolean()
    public showAddressType: boolean = false;

    @bindable.boolean()
    public showArea: boolean = false;

    @bindable.boolean()
    public requireFullAddress: boolean = false;

    @bindable.object()
    public memberSearchFilters: IMemberSearchFilters = {};

    // Make types bindable to accept filtered types from parent
    @bindable({ defaultBindingMode: bindingMode.toView })
    public types: Core.AddressType[] = [];

    @bindable.boolean()
    public required: boolean = false;

    protected regions: ReadonlyArray<Core.AddressRegion> = [];
    protected countries: ReadonlyArray<Core.Country> = [];

    protected validatePostalCode = validatePostalCode;
    protected addressIncomplete = false;

    ////****************** Element References ***************************************////



    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly coreLookupService: CoreLookupService,
        private readonly memberService: MemberService,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        this.loadLookups();
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        // check for preexisting address data to mark fields as required
        this.addressChanged();
        await this.loadLookups();

        if(this.showCountry && !this.address.country) {
            this.address.country = this.countries.find(c => c.code == 'US');
        }
    }

    protected async regionChanged(): Promise<void> {
        this.address.area = null;
        this.addressChanged();
    }

    protected addressChanged(): void {
        if(!this.requireFullAddress) {
            this.addressIncomplete = false;
            return;
        }
        const { street1, street2, city, region, postalCode } = this.address;
        this.addressIncomplete = !!street1 || !!street2 || !!city || !!region || !!postalCode;
    }

    ////****************** Functions ************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        return false;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();

            const regionIncludes = ['areas'];
            const promises = [];

            promises.push(this.coreLookupService.loadRegions(undefined, regionIncludes));
            promises.push(this.coreLookupService.loadCountries());
            // Only load types if none are bound
            if(this.types.length === 0) {
                const types = await this.memberService.loadMemberAddressTypes(undefined, undefined, this.memberSearchFilters);
                this.types = types.results;
            }

            const [regions, countries] = await Promise.all(promises);

            this.regions = regions.results;
            this.countries = countries.results;

        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
